import { ClicksPerPath, Domain, ImpressionsPerPath, Path } from './types'

export type PageClickReportTransaction = {
    pathId: number
    totalTransactions: number
    totalCommission: number
}

export type PageClickReportRow = {
    domain: string
    path: string
    tracked: number
    untracked: number
    total: number
    totalTransactions: number
    totalCommission: number
    impressions: number
}

const refRegex = /^n(\d+)n(\d+)n(\d+)n([A-Za-z0-9]+)$/

export function parsePageClickTransactions(transactions: { ref?: string, totalTransactions: number, totalCommission: number }[]) {
    const formatted: PageClickReportTransaction[] = []

    for (const transaction of transactions) {
        const match = (transaction.ref || '').match(refRegex)

        if (!match) continue

        const [, , , pathIdStr] = match
        const pathId = Number(pathIdStr)

        if (!pathId) continue

        formatted.push({
            pathId,
            totalTransactions: transaction.totalTransactions,
            totalCommission: transaction.totalCommission
        })
    }

    return formatted
}

export function buildPageClickReportRows(opts: {
    domains: Domain[]
    paths: Path[]
    clicksPerPath: ClicksPerPath[]
    badClicksPerPath: ClicksPerPath[]
    impressionsPerPath: ImpressionsPerPath[]
    transactions: PageClickReportTransaction[]
}) {
    const domainById = new Map<number, string>()
    const trackedByPathId = new Map<number, number>()
    const untrackedByPathId = new Map<number, number>()
    const impressionsByPathId = new Map<number, number>()
    const transactionsByPathId = new Map<number, { totalTransactions: number, totalCommission: number }>()

    for (const domain of opts.domains) {
        domainById.set(domain.id, domain.domain)
    }

    for (const click of opts.clicksPerPath) {
        trackedByPathId.set(click.path_id, click.clicks || 0)
    }

    for (const click of opts.badClicksPerPath) {
        untrackedByPathId.set(click.path_id, click.clicks || 0)
    }

    for (const impression of opts.impressionsPerPath) {
        impressionsByPathId.set(impression.path_id, impression.impressions || 0)
    }

    for (const transaction of opts.transactions) {
        const current = transactionsByPathId.get(transaction.pathId) || { totalTransactions: 0, totalCommission: 0 }
        current.totalTransactions += transaction.totalTransactions
        current.totalCommission += transaction.totalCommission
        transactionsByPathId.set(transaction.pathId, current)
    }

    return opts.paths
        .map<PageClickReportRow>((path) => {
            const tracked = trackedByPathId.get(path.id) || 0
            const untracked = untrackedByPathId.get(path.id) || 0
            const total = tracked + untracked
            const transactionSummary = transactionsByPathId.get(path.id) || { totalTransactions: 0, totalCommission: 0 }
            const impressions = impressionsByPathId.get(path.id) || 0

            return {
                domain: domainById.get(path.domain_id) || 'Unknown',
                path: path.path,
                tracked,
                untracked,
                total,
                totalTransactions: transactionSummary.totalTransactions,
                totalCommission: transactionSummary.totalCommission,
                impressions
            }
        })
        .filter((row) => row.total > 0 || row.tracked > 0 || row.impressions > 0)
}